<script setup lang="ts">
/**
 * Button — base interactive element.
 *
 * Variants from prototype (193 occurrences in streama-cms.html):
 *   - kind:    'primary' | 'ghost' | 'danger' | 'subtle'
 *   - size:    'sm' | 'md'
 *   - icon:    optional leading icon component
 *   - loading: shows spinner; disables interaction
 *
 * Renders <button> by default. Pass `as="a"` to render an anchor for
 * link-styled actions; in that case `href` is required and click
 * handlers are bypassed.
 */
import { computed, type Component } from 'vue'

interface Props {
  kind?: 'primary' | 'ghost' | 'danger' | 'subtle'
  size?: 'sm' | 'md'
  icon?: Component
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  as?: 'button' | 'a'
  href?: string
}

const props = withDefaults(defineProps<Props>(), {
  kind: 'subtle',
  size: 'md',
  type: 'button',
  as: 'button',
})

defineEmits<{
  click: [event: MouseEvent]
}>()

const classes = computed(() => {
  const base =
    'inline-flex items-center gap-2 font-medium transition-colors ' +
    'focus-visible:outline-none focus-visible:shadow-focus ' +
    'disabled:opacity-50 disabled:cursor-not-allowed'

  const sizing =
    props.size === 'sm'
      ? 'h-7 px-2.5 text-xs rounded-sm'
      : 'h-9 px-3.5 text-sm rounded'

  const kind = {
    primary:
      'bg-brand text-text-1 hover:bg-brand-hover ' +
      'shadow-glow hover:shadow-glow border border-brand-line',
    ghost:
      'bg-transparent text-text-2 hover:bg-surface-2 hover:text-text-1 ' +
      'border border-border hover:border-border-strong',
    danger:
      'bg-red-soft text-red border border-red-line hover:bg-red hover:text-text-1',
    subtle:
      'bg-surface-2 text-text-2 hover:bg-surface-3 hover:text-text-1 ' +
      'border border-border',
  }[props.kind]

  return `${base} ${sizing} ${kind}`
})
</script>

<template>
  <component
    :is="as"
    :class="classes"
    :type="as === 'button' ? type : undefined"
    :disabled="as === 'button' ? (disabled || loading) : undefined"
    :href="as === 'a' ? href : undefined"
    :aria-busy="loading || undefined"
    @click="(e: MouseEvent) => $emit('click', e)"
  >
    <span
      v-if="loading"
      class="inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin"
      aria-hidden="true"
    />
    <component
      :is="icon"
      v-else-if="icon"
      :size="size === 'sm' ? 12 : 14"
      aria-hidden="true"
    />
    <slot />
  </component>
</template>
